import { create } from 'zustand';

// Default visible columns - show all by default
const DEFAULT_VISIBLE_COLUMNS = [
  'name',
  'driverNumber',
  'status',
  'type',
  'phone',
  'email',
  'address',
  'cdlNumber',
  'cdlState',
  'cdlClass',
  'cdlExpiration',
  'cdlEndorsements',
  'medicalCardNumber',
  'medicalExpiration',
  'hireDate',
  'dateOfBirth',
  'payType',
  'payRate',
  'hosStatus',
  'hosDrivingRemaining',
  'currentLoad',
  'location',
  'invitationAccepted',
  'relayDriverId',
  'ssnLast4',
  'emergencyContact',
];

interface DriversState {
  // Pagination
  page: number;
  pageSize: number;

  // Search
  search: string;

  // Table columns and filters
  visibleColumns: string[];
  columnFilters: Record<string, string>;
}

interface DriversActions {
  // Pagination actions
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;

  // Search actions
  setSearch: (search: string) => void;

  // Column actions
  setVisibleColumns: (columns: string[]) => void;
  toggleColumn: (column: string) => void;

  // Filter actions
  setColumnFilter: (column: string, value: string) => void;
  setColumnFilters: (filters: Record<string, string>) => void;
  clearColumnFilters: () => void;

  // Utility
  resetAll: () => void;
}

type DriversStore = DriversState & DriversActions;

const initialState: DriversState = {
  page: 1,
  pageSize: 25,
  search: '',
  visibleColumns: DEFAULT_VISIBLE_COLUMNS,
  columnFilters: {},
};

export const useDriversStore = create<DriversStore>()((set) => ({
  ...initialState,

  // Pagination actions
  setPage: (page) => set({ page }),
  setPageSize: (pageSize) => set({ pageSize, page: 1 }),

  // Search actions
  setSearch: (search) => set({ search }),

  // Column actions
  setVisibleColumns: (visibleColumns) => set({ visibleColumns }),
  toggleColumn: (column) =>
    set((state) => ({
      visibleColumns: state.visibleColumns.includes(column)
        ? state.visibleColumns.filter((c) => c !== column)
        : [...state.visibleColumns, column],
    })),

  // Filter actions
  setColumnFilter: (column, value) =>
    set((state) => ({
      columnFilters: { ...state.columnFilters, [column]: value },
    })),
  setColumnFilters: (columnFilters) => set({ columnFilters }),
  clearColumnFilters: () => set({ columnFilters: {} }),

  // Utility
  resetAll: () => set(initialState),
}));
